import React, { useEffect, useState } from "react";
import axios from "axios";
import "../styles/Leaderboard.css";
import { jwtDecode } from "jwt-decode";
import { motion, AnimatePresence } from "framer-motion";

const Leaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [clickedButton, setClickedButton] = useState(null);

  const [expandedRow, setExpandedRow] = useState(null);
  const [userPicks, setUserPicks] = useState({});
  const [loadingUserId, setLoadingUserId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  const API_BASE_URL = process.env.REACT_APP_API_URL || "";
  const [currentUserId, setCurrentUserId] = useState(null);

  // Decode JWT for current user ID
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUserId(decoded.userId);
      } catch (e) {
        console.error("Invalid token:", e);
      }
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [lbResp, leaguesResp] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/userstats/leaderboard`, { headers }),
          token
            ? axios.get(`${API_BASE_URL}/api/leagues/my-leagues`, { headers })
            : Promise.resolve({ data: [] }),
        ]);

        setLeaderboardData(lbResp.data);
        setFilteredData(lbResp.data);
        setLeagues(leaguesResp.data || []);
        setSelectedLeague(null);
      } catch (err) {
        console.error("Error fetching leaderboard data:", err);
        setError("Failed to fetch leaderboard or leagues.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const onResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [API_BASE_URL]);

  // Handle league selection & pulse effect
  const handleLeagueSelect = async (league) => {
    const buttonId = league ? league._id : "overall";
    setClickedButton(buttonId); // trigger pulse
    setSelectedLeague(league);
    setLoading(true);

    const token = localStorage.getItem("authToken");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      if (!league) {
        const res = await axios.get(`${API_BASE_URL}/api/userstats/leaderboard`, { headers });
        setFilteredData(res.data);
        setLeaderboardData(res.data);
      } else {
        const res = await axios.get(
          `${API_BASE_URL}/api/userstats/leaderboard?leagueId=${league._id}`,
          { headers }
        );
        const leagueData = res.data.map((u, idx) => ({ ...u, leagueRank: idx + 1 }));
        setFilteredData(leagueData);
        setLeaderboardData(leagueData);
      }
    } catch (err) {
      console.error("Error fetching league leaderboard:", err);
      setError("Failed to fetch leaderboard data.");
    } finally {
      setLoading(false);
      setTimeout(() => setClickedButton(null), 500); // remove pulse after 0.5s
    }
  };

  // Compute colspan for expanded rows
  const computeColSpan = () => {
    let cols = 4; // arrow, rank, username, score
    if (!isMobile) cols += 3; // pointsWagered, conversionRate, pointsAvailable
    if (selectedLeague) cols += 1;
    cols += 1; // potential score
    return cols;
  };

  // Handle expanding rows for user picks
  const handleRowClick = async (index, userId) => {
    const willCollapse = expandedRow === index;
    setExpandedRow(willCollapse ? null : index);
    if (willCollapse || userPicks[userId]) return;

    try {
      setLoadingUserId(userId);
      const res = await axios.get(`${API_BASE_URL}/api/picks/user/${userId}/picks-and-stats`);
      const picks = Array.isArray(res.data.picks) ? res.data.picks : [];
      const normalized = picks.map((p) => ({
        gameName: p.gameName,
        userPick: p.selectedWinner ?? p.userPick,
        pointsWagered: p.pointsWagered ?? p.confidence ?? 0,
        winner: p.winner,
        isCorrect: typeof p.isCorrect === "boolean" ? p.isCorrect : (p.selectedWinner ?? p.userPick) === p.winner,
      }));
      setUserPicks((prev) => ({ ...prev, [userId]: normalized }));
    } catch (err) {
      console.error(`Error loading picks for user ${userId}:`, err);
      setUserPicks((prev) => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingUserId(null);
    }
  };

  if (error) return <div className="leaderboard-error">{error}</div>;

  return (
    <div className="leaderboard-container">
      {/* League Buttons */}
      <div className="league-buttons">
        <button
          className={`league-btn ${!selectedLeague ? "active" : ""} ${clickedButton === "overall" ? "loading" : ""}`}
          onClick={() => handleLeagueSelect(null)}
        >
          Overall
        </button>

        {leagues.map((l) => (
          <button
            key={l._id}
            className={`league-btn ${selectedLeague?._id === l._id ? "active" : ""} ${
              clickedButton === l._id ? "loading" : ""
            }`}
            onClick={() => handleLeagueSelect(l)}
          >
            {l.name}
          </button>
        ))}
      </div>

      {/* Animate table transitions */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedLeague ? selectedLeague._id : "overall"}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th />
                <th>Rank</th>
                {selectedLeague && <th>Conf Rank</th>}
                <th>Username</th>
                <th>Score</th>
                {!isMobile && <th>Points Wagered</th>}
                {!isMobile && <th>Conversion Rate</th>}
                {!isMobile && <th>Points Available</th>}
                {!isMobile && <th>Potential Score</th>}
                {/* <th>Potential Score</th> */}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((user, idx) => {
                const isExpanded = expandedRow === idx;
                const picks = userPicks[user.userId] || [];
                const colSpan = computeColSpan();

                return (
                  <React.Fragment key={user.userId}>
                    <tr
                      className={`leaderboard-row ${isExpanded ? "expanded" : ""} ${
                        user.userId === currentUserId ? "current-user" : ""
                      }`}
                      onClick={() => handleRowClick(idx, user.userId)}
                    >
                      <td className="expand-cell">
                        <span className={`expand-arrow ${isExpanded ? "open" : ""}`} aria-hidden>
                          ▶
                        </span>
                        <span className="sr-only">{isExpanded ? "Collapse" : "Expand"}</span>
                      </td>
                      <td>{user.rank ?? "-"}</td>
                      {selectedLeague && <td>{user.leagueRank ?? "-"}</td>}
                      <td>{user.username}</td>
                      <td>{user.score ?? 0}</td>
                      {!isMobile && <td>{user.pointsWagered ?? 0}</td>}
                      {!isMobile && <td>{user.conversionRate ?? "-"}</td>}
                      {!isMobile && <td>{user.pointsAvailable ?? 0}</td>}
                      {!isMobile && <td>{(user.score ?? 0) + (user.pointsAvailable ?? 0)}</td>}
                    </tr>

                    {isExpanded && (
                      <tr className="expanded-row">
                        <td colSpan={colSpan}>
                          <div className="expanded-content">
                              {isMobile && (
                                <table className="expanded-stats-table">
                                    <tbody>
                                    <tr>
                                        <td>Points Wagered:</td>
                                        <td>{user.pointsWagered ?? 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Conversion Rate:</td>
                                        <td>{user.conversionRate ?? '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Points Available:</td>
                                        <td>{user.pointsAvailable ?? 0}</td>
                                    </tr>
                                      <tr>
                                        <td>Potential Score:</td>
                                        <td>{(user.score ?? 0) + (user.pointsAvailable ?? 0)}</td>
                                      </tr>
                                    </tbody>
                                </table>
                                )}

                            {loadingUserId === user.userId ? (
                              <div>Loading picks...</div>
                            ) : picks.length === 0 ? (
                              <div>No picks available for this user.</div>
                            ) : (
                              <table className="picks-table">
                                <thead>
                                  <tr className="expanded-tr">
                                    <th>Game</th>
                                    <th>Pick</th>
                                    <th>Wagered</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {picks.map((p, i) => {
                                    const isCorrect =
                                      typeof p.isCorrect === "boolean"
                                        ? p.isCorrect
                                        : (p.userPick ?? p.selectedWinner) === p.winner;

                                    let rowClass = "";
                                    if (p.winner === "TBD") rowClass = "gray-italic";
                                    else if (!isCorrect) rowClass = "incorrect";

                                    return (
                                      <tr key={i} className={rowClass}>
                                        <td>{p.gameName}</td>
                                        <td>{p.userPick}</td>
                                        <td className={`points-wagered ${isCorrect ? "correct" : ""}`}>
                                          {p.pointsWagered}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Leaderboard;
